/**
 * useAIChat Hook
 * Manages the state and logic for the AI chat conversation.
 * Handles message history, tool calls, confirmation flow, and lesson storage.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAppContext } from '../contexts/Storage';
import { TRANSACTIONS_QUERY_KEY, TRANSACTION_ENTITIES_QUERY_KEY } from '../api/transactions';
import { CREDIT_BALANCE_QUERY_KEY } from '../api/credits';
import { BALANCE_SHEET_QUERY_KEY, LEDGERS_QUERY_KEY } from '../api/accounts';
import {
  aiProvider,
  assembleSystemPrompt,
  allTools,
  toolRequiresConfirmation,
  createToolHandlers,
  createActionHandlers,
  searchKnowledge,
  type ChatMessage,
  type ToolCall,
  type ToolResult,
} from '../ai';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ChatMessageDisplay {
  id: string;
  role: 'user' | 'assistant' | 'system' | 'tool';
  content: string;
  isLoading?: boolean;
  isStreaming?: boolean;
  status?: string | null;
  toolCalls?: ToolCall[];
  toolResults?: ToolResultDisplay[];
  timestamp: Date;
}

export interface ToolResultDisplay {
  toolName: string;
  toolCallId?: string;
  result: ToolResult;
}

export interface PendingConfirmation {
  id: string;
  description: string;
  toolCall: ToolCall;
  /** Batch of similar actions confirmed together */
  toolCalls: ToolCall[];
  /** Remaining tool calls after this batch (may need another confirm) */
  remainingToolCalls?: ToolCall[];
  humanReadableAction: string;
  /** Short header, e.g. "Removing payment method" */
  title?: string;
  sentence?: string;
  booksName?: string | null;
  date?: string | null;
  allowsDate?: boolean;
  newTerms?: string[];
  precedingResults?: ToolResultDisplay[];
}

/** Retry is only for network / transport failures — not business errors */
export type FailedChatAction =
  | { type: 'message'; content: string; reason: 'network' }
  | { type: 'confirm'; pending: PendingConfirmation; reason: 'network' };

export interface Lesson {
  id: string;
  topic: string;
  content: string;
  createdAt: string;
  lastAccessed: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STORAGE_LESSONS_KEY = 'balancio_ai_lessons';
const STORAGE_MESSAGES_KEY = 'balancio_ai_messages';
const MAX_HISTORY_MESSAGES = 50;

// ---------------------------------------------------------------------------
// Helper
// ---------------------------------------------------------------------------

function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
}

function toolStatus(toolName: string): string {
  if (toolName.includes('entit')) return 'Looking up terms';
  if (toolName.includes('transaction') || toolName.includes('record')) return 'Reading books';
  if (toolName.includes('dashboard') || toolName.includes('ledger') || toolName.includes('balance') || toolName.includes('profit')) {
    return 'Reading figures';
  }
  if (toolName.startsWith('create_') || toolName.startsWith('update_') || toolName.startsWith('delete_')) {
    return 'Preparing to save';
  }
  return 'Working';
}

/** Client-facing summary when the model fails after tools already ran. */
function summarizeCompletedTools(results: ToolResultDisplay[]): string {
  const successes = results
    .filter((r) => r.result?.success)
    .map((r) => (r.result.formattedMessage || '').trim())
    .filter(Boolean);
  const failures = results
    .filter((r) => !r.result?.success)
    .map((r) => (r.result?.error || '').trim())
    .filter(Boolean)
    .map((msg) => {
      const lower = msg.toLowerCase();
      if (lower.includes('sqlstate') || lower.includes('duplicate entry') || lower.includes('integrity constraint')) {
        return 'That name is already in use. Choose a different name or select the existing one.';
      }
      return msg;
    });

  if (successes.length > 0 && failures.length === 0) {
    return successes.join('\n');
  }
  if (successes.length > 0 && failures.length > 0) {
    return `${successes.join('\n')}\n\nSome steps could not finish: ${failures.join('; ')}`;
  }
  if (failures.length > 0) {
    return `I could not complete that. ${failures.join('; ')}`;
  }
  return 'Done.';
}

/**
 * Pair tool results to tool_call ids. Prefer explicit toolCallId; fall back to same-index.
 */
function pairToolResults(
  toolCalls: ToolCall[],
  toolResults: ToolResultDisplay[]
): { toolCall: ToolCall; result: ToolResult }[] {
  const pairs: { toolCall: ToolCall; result: ToolResult }[] = [];
  const usedResultIndexes = new Set<number>();

  for (const tc of toolCalls) {
    let idx = toolResults.findIndex(
      (tr, i) => !usedResultIndexes.has(i) && tr.toolCallId === tc.id
    );
    if (idx < 0) {
      idx = toolResults.findIndex(
        (tr, i) => !usedResultIndexes.has(i) && tr.toolName === tc.function.name
      );
    }
    if (idx < 0) continue;
    usedResultIndexes.add(idx);
    pairs.push({ toolCall: tc, result: toolResults[idx].result });
  }

  return pairs;
}

/**
 * Ensure every assistant tool_calls message is followed by matching tool messages.
 * Incomplete turns are collapsed to plain assistant text so DeepSeek never rejects history.
 */
function sanitizeHistoryForTools(history: ChatMessage[]): ChatMessage[] {
  const out: ChatMessage[] = [];

  for (let i = 0; i < history.length; i++) {
    const msg = history[i];

    if (msg.role !== 'assistant' || !msg.tool_calls?.length) {
      // Drop orphan tool messages (no preceding assistant tool_calls)
      if (msg.role === 'tool') continue;
      out.push(msg);
      continue;
    }

    const toolCallIds = msg.tool_calls.map((tc) => tc.id);
    const following: ChatMessage[] = [];
    let j = i + 1;
    while (j < history.length && history[j].role === 'tool') {
      following.push(history[j]);
      j++;
    }

    const byId = new Map(
      following
        .filter((t) => t.tool_call_id)
        .map((t) => [t.tool_call_id as string, t])
    );
    const complete = toolCallIds.every((id) => byId.has(id));

    if (!complete) {
      // Incomplete protocol turn — keep readable content only
      const text =
        (msg.content || '').trim() ||
        following
          .map((t) => {
            try {
              const parsed = JSON.parse(String(t.content || '{}'));
              return parsed.formattedMessage || parsed.error || '';
            } catch {
              return '';
            }
          })
          .filter(Boolean)
          .join('\n') ||
        'Done.';
      out.push({ role: 'assistant', content: text });
      i = j - 1;
      continue;
    }

    out.push(msg);
    for (const id of toolCallIds) {
      out.push(byId.get(id)!);
    }
    i = j - 1;
  }

  return out;
}

function collectEntityNames(results: ToolResultDisplay[]): Map<number, string> {
  const map = new Map<number, string>();
  const walk = (value: unknown) => {
    if (!value) return;
    if (Array.isArray(value)) {
      value.forEach(walk);
      return;
    }
    if (typeof value !== 'object') return;
    const row = value as Record<string, unknown>;
    const id = Number(row.id ?? row.transaction_entity_id);
    const name = String(row.transaction_entity_name ?? row.transaction_entity_past_tense ?? '').trim();
    if (id > 0 && name) map.set(id, name);
    Object.values(row).forEach(walk);
  };
  results.forEach((r) => walk(r.result?.data));
  return map;
}

function isNetworkFailure(error: unknown): boolean {
  const msg = String((error as { message?: string })?.message || error || '').toLowerCase();
  if (!msg) return false;
  return (
    msg.includes('failed to fetch') ||
    msg.includes('network') ||
    msg.includes('timeout') ||
    msg.includes('timed out') ||
    msg.includes('net::') ||
    msg.includes('econnrefused') ||
    msg.includes('enotfound') ||
    msg.includes('load failed') ||
    /\b(502|503|504)\b/.test(msg)
  );
}

/** Group confirmable tools that can share one confirmation */
function toolBatchGroup(toolName: string): string {
  if (toolName === 'create_entity') return 'create_entity';
  if (toolName === 'update_entity') return 'update_entity';
  if (toolName === 'delete_entity') return 'delete_entity';
  if (toolName === 'create_transaction') return 'create_transaction';
  if (toolName === 'update_transaction') return 'update_transaction';
  if (toolName === 'delete_transaction') return 'delete_transaction';
  return toolName;
}

function describePendingAction(
  toolCall: ToolCall,
  preceding: ToolResultDisplay[],
  booksName?: string | null
): { title: string; sentence: string; date?: string; allowsDate: boolean; newTerms: string[] } {
  let args: Record<string, unknown> = {};
  try {
    args = JSON.parse(toolCall.function.arguments || '{}');
  } catch {
    args = {};
  }
  const names = collectEntityNames(preceding);
  const label = (id: unknown) => names.get(Number(id)) || '';
  const quoted = (value: unknown) => {
    const text = String(value ?? '').trim();
    return text ? `"${text}"` : '';
  };
  const categoryLabel = (category: unknown) => {
    switch (String(category || '').toLowerCase()) {
      case 'media':
        return 'payment method';
      case 'action':
        return 'action';
      case 'object':
        return 'item';
      case 'indirect_object':
        return 'party';
      default:
        return 'term';
    }
  };

  if (toolCall.function.name === 'create_transaction') {
    const action = label(args.transaction_action_id) || 'Event';
    const object = label(args.transaction_object_id);
    const media = label(args.transaction_media_id);
    const amount = args.transaction_total_amount;
    const qty = args.transaction_quantity;
    const parts = [action, object, amount != null ? String(amount) : '']
      .filter(Boolean)
      .join(' ');
    const via = media ? ` via ${media}` : '';
    const qtyBit = qty != null ? ` · qty ${qty}` : '';
    const when = typeof args.transaction_date === 'string' && args.transaction_date
      ? ` on ${args.transaction_date}`
      : '';
    return {
      title: 'Saving event',
      sentence: `${parts}${via}${qtyBit}${when}`.trim() || 'Save this event',
      allowsDate: false,
      newTerms: [],
    };
  }
  if (toolCall.function.name === 'create_entity') {
    const name = String(args.transaction_entity_name || '').trim() || 'unnamed';
    const kind = categoryLabel(args.transaction_entity_category);
    return {
      title: `Adding ${kind}`,
      sentence: `Add ${kind} ${quoted(name)} to these books`,
      allowsDate: false,
      newTerms: [name],
    };
  }
  if (toolCall.function.name === 'create_record') {
    const name = String(args.record_name || '').trim();
    return {
      title: 'Creating books',
      sentence: name ? `Create books ${quoted(name)}` : 'Create these books',
      allowsDate: false,
      newTerms: [],
    };
  }
  if (toolCall.function.name === 'update_transaction') {
    const action = label(args.transaction_action_id);
    const object = label(args.transaction_object_id);
    const media = label(args.transaction_media_id);
    const amount = args.transaction_total_amount;
    const detail = [action, object, amount != null ? String(amount) : ''].filter(Boolean).join(' ');
    const via = media ? ` via ${media}` : '';
    return {
      title: 'Updating event',
      sentence: detail ? `Update event: ${detail}${via}` : 'Update this event',
      allowsDate: false,
      newTerms: [],
    };
  }
  if (toolCall.function.name === 'delete_transaction') {
    return { title: 'Deleting event', sentence: 'Delete this event', allowsDate: false, newTerms: [] };
  }
  if (toolCall.function.name === 'delete_entity') {
    const name = String(args.transaction_entity_name || label(args.entity_id) || '').trim();
    const kind = categoryLabel(args.transaction_entity_category);
    return {
      title: `Removing ${kind}`,
      sentence: name
        ? `Remove ${kind} ${quoted(name)}`
        : `Remove this ${kind}`,
      allowsDate: false,
      newTerms: [],
    };
  }
  if (toolCall.function.name === 'update_entity') {
    const name = String(args.transaction_entity_name || '').trim();
    const kind = categoryLabel(args.transaction_entity_category);
    return {
      title: `Updating ${kind}`,
      sentence: name
        ? `Update ${kind} to ${quoted(name)}`
        : `Update this ${kind}`,
      allowsDate: false,
      newTerms: [],
    };
  }
  if (toolCall.function.name === 'update_record') {
    const name = String(args.record_name || '').trim();
    return {
      title: 'Updating books',
      sentence: name ? `Rename books to ${quoted(name)}` : 'Update these books',
      allowsDate: false,
      newTerms: [],
    };
  }
  if (toolCall.function.name === 'delete_record') {
    const name = String(args.record_name || booksName || '').trim();
    return {
      title: 'Deleting books',
      sentence: name ? `Delete books ${quoted(name)}` : 'Delete these books',
      allowsDate: false,
      newTerms: [],
    };
  }
  if (toolCall.function.name === 'join_record') {
    return { title: 'Joining books', sentence: 'Join books with that invite code', allowsDate: false, newTerms: [] };
  }
  if (toolCall.function.name === 'grant_permission') {
    return { title: 'Updating access', sentence: 'Update access for that person', allowsDate: false, newTerms: [] };
  }
  return {
    title: 'Confirm action',
    sentence: booksName ? `Save this in ${booksName}` : 'Save this',
    allowsDate: false,
    newTerms: [],
  };
}

function describePendingBatch(
  toolCalls: ToolCall[],
  preceding: ToolResultDisplay[],
  booksName?: string | null
): { title: string; sentence: string; allowsDate: boolean; newTerms: string[] } {
  if (toolCalls.length === 1) {
    const one = describePendingAction(toolCalls[0], preceding, booksName);
    return {
      title: one.title,
      sentence: one.sentence,
      allowsDate: false,
      newTerms: one.newTerms,
    };
  }

  const parts = toolCalls.map((tc) => describePendingAction(tc, preceding, booksName));
  const baseTitle = parts[0].title;
  const title = `${baseTitle} (${toolCalls.length})`;
  const sentence = parts.map((p, i) => `${i + 1}. ${p.sentence}`).join('\n');
  const newTerms = parts.flatMap((p) => p.newTerms);
  return { title, sentence, allowsDate: false, newTerms };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAIChat() {
  const queryClient = useQueryClient();
  const appContext = useAppContext();
  const { token, user, selectedRecord } = appContext;

  // Load messages from localStorage on initialization
  const [messages, setMessages] = useState<ChatMessageDisplay[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_MESSAGES_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Restore timestamps as Date objects
        return parsed.map((m: any) => ({
          ...m,
          timestamp: new Date(m.timestamp),
        }));
      }
    } catch { }
    return [];
  });

  const [isProcessing, setIsProcessing] = useState(false);
  const [pendingConfirmation, setPendingConfirmation] = useState<PendingConfirmation | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [composerDraft, setComposerDraft] = useState('');
  const [agentStatus, setAgentStatus] = useState<string | null>(null);
  const [failedAction, setFailedAction] = useState<FailedChatAction | null>(null);

  // Refs for non-reactive access
  const messagesRef = useRef<ChatMessageDisplay[]>([]);
  const processingRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  const cancelledRef = useRef(false);
  const sendMessageRef = useRef<((content: string) => Promise<void>) | null>(null);
  const confirmActionRef = useRef<(() => Promise<void>) | null>(null);

  const isAbortError = (err: unknown) => {
    if (!err) return false;
    if (cancelledRef.current) return true;
    const name = (err as { name?: string })?.name;
    const message = String((err as { message?: string })?.message || '').toLowerCase();
    return name === 'AbortError' || message.includes('aborted') || message.includes('abort');
  };

  const beginRun = () => {
    cancelledRef.current = false;
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    setFailedAction(null);
    setError(null);
  };

  const chatSignal = () => abortRef.current?.signal;

  // Keep refs in sync
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Persist messages to localStorage whenever they change
  useEffect(() => {
    try {
      const durable = messages
        .filter((m) => !m.isLoading || !!m.content)
        .map((m) => ({ ...m, isLoading: false, isStreaming: false, status: null }));
      if (durable.length > 0) {
        localStorage.setItem(STORAGE_MESSAGES_KEY, JSON.stringify(durable));
      } else {
        localStorage.removeItem(STORAGE_MESSAGES_KEY);
      }
    } catch { }
  }, [messages]);

  useEffect(() => {
    processingRef.current = isProcessing;
  }, [isProcessing]);

  // ============ Lessons Management ============

  const getStoredLessons = useCallback((): Lesson[] => {
    try {
      const stored = localStorage.getItem(STORAGE_LESSONS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }, []);

  const storeLesson = useCallback((topic: string, content: string): void => {
    const lessons = getStoredLessons();
    const newLesson: Lesson = {
      id: generateId(),
      topic,
      content,
      createdAt: new Date().toISOString(),
      lastAccessed: new Date().toISOString(),
    };
    lessons.push(newLesson);
    localStorage.setItem(STORAGE_LESSONS_KEY, JSON.stringify(lessons));
  }, [getStoredLessons]);

  const retrieveLessons = useCallback((query: string): Lesson[] => {
    const lessons = getStoredLessons();
    const searchTerms = query.toLowerCase().split(/\s+/).filter(t => t.length > 2);

    return lessons.filter(lesson => {
      const searchText = `${lesson.topic} ${lesson.content}`.toLowerCase();
      return searchTerms.some(term => searchText.includes(term));
    }).slice(0, 5);
  }, [getStoredLessons]);

  const getLessonsContextString = useCallback((): string => {
    const lessons = getStoredLessons();
    if (lessons.length === 0) return '';
    return lessons.map(l => `- [${l.topic}]: ${l.content}`).join('\n');
  }, [getStoredLessons]);

  const promptContext = useCallback(() => ({
    userName: user?.username || user?.firstName || undefined,
    currentPage: window.location.pathname,
    lessonsContext: getLessonsContextString() || undefined,
    booksName: selectedRecord?.record_name,
    booksId: selectedRecord?.id,
  }), [user, selectedRecord, getLessonsContextString]);

  // ============ Core Chat Logic ============

  const addMessage = useCallback((message: ChatMessageDisplay) => {
    setMessages(prev => {
      const updated = [...prev, message];
      if (updated.length > MAX_HISTORY_MESSAGES) {
        return updated.slice(updated.length - MAX_HISTORY_MESSAGES);
      }
      return updated;
    });
  }, []);

  const updateLastMessage = useCallback((updates: Partial<ChatMessageDisplay>) => {
    setMessages(prev => {
      const updated = [...prev];
      const lastIndex = updated.length - 1;
      if (lastIndex >= 0) {
        updated[lastIndex] = { ...updated[lastIndex], ...updates };
      }
      return updated;
    });
  }, []);

  const patchMessage = useCallback((id: string, updates: Partial<ChatMessageDisplay>) => {
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...updates } : m)));
  }, []);

  const setWorking = useCallback((status: string | null) => {
    setAgentStatus(status);
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (!last || last.role !== 'assistant') return prev;
      const updated = [...prev];
      // Keep thinking UI visible whenever the agent is still working
      updated[updated.length - 1] = {
        ...last,
        isLoading: !!status,
        status,
      };
      return updated;
    });
  }, []);

  /**
   * Build chat history for the API request.
   * Never emits assistant tool_calls without a complete set of matching tool replies.
   */
  const buildChatHistory = useCallback((
    extraResults?: { toolName: string; toolCallId: string; result: ToolResult }[]
  ): ChatMessage[] => {
    const history: ChatMessage[] = [];

    history.push({
      role: 'system',
      content: assembleSystemPrompt(promptContext()),
    });

    const addedToolCallIds = new Set<string>();

    for (const msg of messagesRef.current) {
      if (msg.isLoading && !msg.content && !msg.toolCalls?.length) {
        continue;
      }
      if (msg.role === 'user') {
        history.push({ role: 'user', content: msg.content });
        continue;
      }
      if (msg.role !== 'assistant') continue;

      const pairs =
        msg.toolCalls?.length && msg.toolResults?.length
          ? pairToolResults(msg.toolCalls, msg.toolResults)
          : [];

      if (pairs.length > 0) {
        history.push({
          role: 'assistant',
          content: msg.content || '',
          tool_calls: pairs.map((p) => p.toolCall),
        });
        for (const pair of pairs) {
          if (addedToolCallIds.has(pair.toolCall.id)) continue;
          addedToolCallIds.add(pair.toolCall.id);
          history.push({
            role: 'tool',
            content: JSON.stringify(pair.result),
            tool_call_id: pair.toolCall.id,
          });
        }
      } else if ((msg.content || '').trim()) {
        // Pending tool_calls without results (e.g. awaiting confirm) → text only
        history.push({ role: 'assistant', content: msg.content });
      }
      // Else: bare tool_calls with no content/results — skip (incomplete turn)
    }

    if (extraResults && extraResults.length > 0) {
      const neededResults = extraResults.filter((er) => !addedToolCallIds.has(er.toolCallId));

      if (neededResults.length > 0) {
        const lastAssistant = [...history].reverse().find((m) => m.role === 'assistant');
        const neededIds = new Set(neededResults.map((er) => er.toolCallId));
        const assistantCoversAll = neededResults.every((er) =>
          lastAssistant?.tool_calls?.some((tc) => tc.id === er.toolCallId)
        );

        if (!assistantCoversAll) {
          // Drop any incomplete trailing assistant tool_calls for these ids
          if (lastAssistant?.tool_calls?.some((tc) => neededIds.has(tc.id))) {
            history.pop();
          }
          history.push({
            role: 'assistant',
            content: '',
            tool_calls: neededResults.map((er) => ({
              id: er.toolCallId,
              type: 'function' as const,
              function: { name: er.toolName, arguments: '{}' },
            })),
          });
        }

        for (const er of neededResults) {
          addedToolCallIds.add(er.toolCallId);
          history.push({
            role: 'tool',
            content: JSON.stringify(er.result),
            tool_call_id: er.toolCallId,
          });
        }
      }
    }

    return sanitizeHistoryForTools(history);
  }, [promptContext]);

  /**
   * Execute a tool call and return the result
   */
  const executeTool = useCallback(async (toolCall: ToolCall): Promise<ToolResult> => {
    const toolName = toolCall.function.name;
    const args = toolCall.function.arguments;

    // Handle knowledge tools directly (don't need API calls)
    if (toolName === 'store_lesson') {
      try {
        const { topic, content } = JSON.parse(args);
        storeLesson(topic, content);
        return {
          success: true,
          data: null,
          formattedMessage: `Lesson "${topic}" stored successfully.`,
        };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    }

    if (toolName === 'retrieve_lessons') {
      try {
        const { query } = JSON.parse(args);
        const lessons = retrieveLessons(query);
        return {
          success: true,
          data: lessons,
          formattedMessage: lessons.length > 0
            ? `Found ${lessons.length} relevant lesson(s):\n${lessons.map((l: any) => `- [${l.topic}]: ${l.content}`).join('\n')}`
            : 'No relevant lessons found.',
        };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    }

    if (toolName === 'navigate_to_page' || toolName === 'go_to_profile' || toolName === 'refresh_page') {
      return {
        success: true,
        data: null,
        formattedMessage: 'I cannot move the screen. Ask me about these books instead.',
      };
    }

    if (toolName === 'toggle_dark_mode') {
      return {
        success: true,
        data: null,
        formattedMessage: 'You can switch theme from the sidebar.',
      };
    }

    if (toolName === 'stop_chain') {
      try {
        const { message } = JSON.parse(args);
        return {
          success: true,
          data: { __stopChain: true },
          formattedMessage: message || '',
        };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    }

    // Handle search knowledge tool
    if (toolName === 'answer_from_knowledge_base') {
      try {
        const { query } = JSON.parse(args);
        const results = searchKnowledge(query);
        return {
          success: true,
          data: results,
          formattedMessage: results.length > 0
            ? results.map(r => `**${r.topic}**: ${r.content}`).join('\n\n---\n\n')
            : 'I could not find specific information about that in my knowledge base.',
        };
      } catch (error: any) {
        return { success: false, data: null, error: error.message };
      }
    }

    // For all other tools, use the service handlers
    try {
      // Check if it's an action tool (needs handler from actionHandlers)

      if (toolRequiresConfirmation(toolName)) {
        const actionHandlers = createActionHandlers(appContext);
        const actionHandler = (actionHandlers as any)[toolName];
        if (actionHandler) {
          return await actionHandler(args);
        }
      }

      // Otherwise use the read tool handlers
      const handlers = createToolHandlers(appContext);
      const handler = (handlers as any)[toolName];
      if (!handler) {
        throw new Error(`Unknown tool: ${toolName}`);
      }
      return await handler(args);
    } catch (error: any) {
      return { success: false, data: null, error: error.message };
    }
  }, [storeLesson, retrieveLessons, appContext]);

  /**
   * Process tool calls sequentially. Auto-run read tools;
   * pause on the first confirmable batch (similar tools grouped).
   */
  const processToolCalls = useCallback(async (
    toolCalls: ToolCall[]
  ): Promise<{ results: ToolResultDisplay[]; needsConfirmation: PendingConfirmation | null }> => {
    const results: ToolResultDisplay[] = [];
    let confirmation: PendingConfirmation | null = null;

    for (let i = 0; i < toolCalls.length; i++) {
      const toolCall = toolCalls[i];
      const toolName = toolCall.function.name;
      setWorking(toolStatus(toolName));

      if (toolRequiresConfirmation(toolName)) {
        const group = toolBatchGroup(toolName);
        const batch: ToolCall[] = [toolCall];
        let j = i + 1;
        while (
          j < toolCalls.length &&
          toolRequiresConfirmation(toolCalls[j].function.name) &&
          toolBatchGroup(toolCalls[j].function.name) === group
        ) {
          batch.push(toolCalls[j]);
          j += 1;
        }
        const remaining = toolCalls.slice(j);
        const copy = describePendingBatch(batch, results, selectedRecord?.record_name);
        confirmation = {
          id: generateId(),
          description: copy.sentence,
          toolCall: batch[0],
          toolCalls: batch,
          remainingToolCalls: remaining.length > 0 ? remaining : undefined,
          humanReadableAction: copy.sentence,
          title: copy.title,
          sentence: copy.sentence,
          booksName: selectedRecord?.record_name,
          allowsDate: false,
          newTerms: copy.newTerms,
          precedingResults: results.length > 0 ? [...results] : undefined,
        };
        break;
      }

      if (cancelledRef.current) {
        break;
      }

      const result = await executeTool(toolCall);
      results.push({ toolName, toolCallId: toolCall.id, result });
    }

    return { results, needsConfirmation: confirmation };
  }, [executeTool, selectedRecord, setWorking]);

  /**
   * Send tool results back to the AI for a final response
   * Uses the original assistant message (before toolResults were added) to build
   * a clean history that matches DeepSeek's required tool_call protocol.
   */
  const sendToolResultsToAI = useCallback(async (
    results: ToolResultDisplay[],
    originalAssistantMsg: ChatMessageDisplay
  ): Promise<ChatMessageDisplay> => {
    // Build the extra results with their correct tool_call_ids
    const extraResults = results.map((tr, i) => ({
      toolName: tr.toolName,
      toolCallId: tr.toolCallId || originalAssistantMsg.toolCalls?.[i]?.id || tr.toolName,
      result: tr.result,
    }));

    // Add tool results to the assistant message in state AND messagesRef (sync for history)
    const updatedAssistantMsg: ChatMessageDisplay = {
      ...originalAssistantMsg,
      toolResults: results,
    };

    messagesRef.current = messagesRef.current.map((m) =>
      m.id === originalAssistantMsg.id ? updatedAssistantMsg : m
    );
    setMessages((prev) => {
      const updated = [...prev];
      const idx = updated.findIndex((m) => m.id === originalAssistantMsg.id);
      if (idx >= 0) {
        updated[idx] = updatedAssistantMsg;
      }
      return updated;
    });

    // Build protocol-safe history (pairs tool_calls with tool replies)
    const history = buildChatHistory(extraResults);

    // Get AI's final response
    setWorking('Writing');
    // Drop leftover thinking state on older bubbles
    setMessages((prev) =>
      prev.map((m) =>
        m.id === originalAssistantMsg.id || !m.isLoading
          ? m
          : { ...m, isLoading: false, status: null }
      )
    );
    let response;
    try {
      response = await aiProvider.chat({
        messages: history,
        tools: allTools,
      }, token || undefined, chatSignal());
    } catch (error: any) {
      if (isAbortError(error)) {
        throw error;
      }
      // === AUTOMATIC RECOVERY: Try to recover from ANY API error ===
      // This handles tool protocol mismatches, invalid schema errors, etc.
      // by rebuilding history WITHOUT tool results and retrying.
      try {
        // Rebuild history without the extra tool results
        const recoveryHistory = buildChatHistory();
        // Add just the user's latest intent as a summary
        recoveryHistory.push({
          role: 'user',
          content: 'Please continue with the result of the action that was just completed.'
        });
        response = await aiProvider.chat({
          messages: recoveryHistory,
          tools: allTools,
        }, token || undefined, chatSignal());
      } catch (recoveryError: any) {
        // If recovery also fails, throw the original error
        throw error;
      }
    }

    const responseMsg = response.choices[0]?.message;

    const finalMessage: ChatMessageDisplay = {
      id: generateId(),
      role: 'assistant',
      content: responseMsg.content || '',
      timestamp: new Date(),
      toolCalls: responseMsg.tool_calls,
    };

    setMessages(prev => [...prev, finalMessage]);

    // If the response has more tool calls, process them recursively
    if (responseMsg.tool_calls && responseMsg.tool_calls.length > 0) {
      await handleToolCallsInMessage(finalMessage);
    }

    return finalMessage;
  }, [buildChatHistory, token, setWorking]);

  /**
   * Handle tool calls in a message - processes them and potentially waits for confirmation.
   * Returns true when paused waiting for user confirm (caller must keep processing UI).
   */
  const handleToolCallsInMessage = useCallback(async (message: ChatMessageDisplay): Promise<boolean> => {
    if (!message.toolCalls || message.toolCalls.length === 0) return false;

    setIsProcessing(true);
    let pausedForConfirm = false;

    try {
      if (cancelledRef.current) return false;

      const { results, needsConfirmation } = await processToolCalls(message.toolCalls);

      if (cancelledRef.current) {
        updateLastMessage({
          content: message.content || 'Stopped.',
          toolResults: results,
          isLoading: false,
          status: null,
        });
        return false;
      }

      if (needsConfirmation) {
        pausedForConfirm = true;
        setPendingConfirmation(needsConfirmation);
        setWorking('Waiting for confirm');
        updateLastMessage({
          content: message.content || 'Confirm below to continue.',
          toolResults: results,
          isLoading: true,
          status: 'Waiting for confirm',
        });
      } else {
        const hasStopChain = results.some(
          r => r.result?.data && (r.result.data as any).__stopChain === true
        );

        if (hasStopChain) {
          updateLastMessage({
            toolResults: results,
            isLoading: false,
            status: null,
          });
        } else {
          try {
            await sendToolResultsToAI(results, message);
          } catch (sendError: any) {
            if (isAbortError(sendError)) {
              updateLastMessage({
                content: message.content || 'Stopped.',
                toolResults: results,
                isLoading: false,
                status: null,
              });
              return false;
            }
            updateLastMessage({
              content: message.content,
              toolResults: results,
            });
            const recoveryMsg: ChatMessageDisplay = {
              id: generateId(),
              role: 'assistant',
              content: summarizeCompletedTools(results),
              timestamp: new Date(),
            };
            setMessages(prev => [...prev, recoveryMsg]);
          }
        }
      }
    } catch (error: any) {
      if (isAbortError(error)) {
        updateLastMessage({
          content: message.content || 'Stopped.',
          isLoading: false,
          status: null,
        });
        return false;
      }
      setError(error.message);
      updateLastMessage({
        content: message.content
          ? `${message.content}\n\nSomething went wrong. Please try again.`
          : 'Something went wrong. Please try again.',
        isLoading: false,
        status: null,
      });
    } finally {
      // Always unlock so Confirm/Cancel stay clickable; keep thinking status if waiting
      setIsProcessing(false);
      if (!pausedForConfirm) {
        setWorking(null);
      }
    }
    return pausedForConfirm;
  }, [processToolCalls, sendToolResultsToAI, updateLastMessage, setWorking]);

  /**
   * Confirm and execute a pending batch of similar actions
   */
  const confirmAction = useCallback(async () => {
    if (!pendingConfirmation) return;

    const confirmationSnapshot = pendingConfirmation;
    const { precedingResults, remainingToolCalls } = pendingConfirmation;
    const batch =
      confirmationSnapshot.toolCalls?.length > 0
        ? confirmationSnapshot.toolCalls
        : [confirmationSnapshot.toolCall];

    setPendingConfirmation(null);
    beginRun();
    setIsProcessing(true);
    // Clear prior waiting state on the bubble before showing Saving
    setMessages((prev) =>
      prev.map((m) => (m.isLoading ? { ...m, isLoading: false, status: null } : m))
    );
    setWorking('Saving');

    let pausedForConfirm = false;

    try {
      const batchResults: ToolResultDisplay[] = [];
      let batchFailed = false;

      for (const toolCall of batch) {
        if (cancelledRef.current) {
          updateLastMessage({
            content: 'Stopped.',
            isLoading: false,
            status: null,
          });
          return;
        }

        setWorking(toolStatus(toolCall.function.name) || 'Saving');
        const result = await executeTool(toolCall);
        batchResults.push({
          toolName: toolCall.function.name,
          toolCallId: toolCall.id,
          result,
        });

        if (!result.success) {
          batchFailed = true;
          break;
        }
      }

      const results: ToolResultDisplay[] = [
        ...(precedingResults || []),
        ...batchResults,
      ];

      if (batchFailed) {
        // Business failure — let the agent explain what happened (no Retry button)
        const lastMsg = messagesRef.current[messagesRef.current.length - 1];
        if (lastMsg) {
          try {
            await sendToolResultsToAI(results, lastMsg);
          } catch (followUpError: any) {
            if (!isAbortError(followUpError)) {
              const failMsg =
                batchResults[batchResults.length - 1]?.result?.error ||
                'That did not save. Please try again or change the details.';
              updateLastMessage({
                toolResults: results,
                content: failMsg,
                isLoading: false,
                status: null,
              });
            }
          }
        }
        return;
      }

      queryClient.invalidateQueries({ queryKey: TRANSACTIONS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: TRANSACTION_ENTITIES_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: CREDIT_BALANCE_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: BALANCE_SHEET_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: LEDGERS_QUERY_KEY });
      window.dispatchEvent(new CustomEvent('balancio-books-updated'));

      const lastMsg = messagesRef.current[messagesRef.current.length - 1];
      if (!lastMsg) return;

      const hasStopChain = batchResults.some(
        (r) => r.result?.data && (r.result.data as any).__stopChain === true
      );
      if (hasStopChain) {
        updateLastMessage({ toolResults: results, isLoading: false, status: null });
        return;
      }

      // Continue with remaining tools (may need another confirmation)
      if (remainingToolCalls && remainingToolCalls.length > 0) {
        const { results: moreResults, needsConfirmation } = await processToolCalls(remainingToolCalls);
        const allResults = [...results, ...moreResults];

        if (cancelledRef.current) {
          updateLastMessage({
            content: 'Stopped.',
            toolResults: allResults,
            isLoading: false,
            status: null,
          });
          return;
        }

        if (needsConfirmation) {
          pausedForConfirm = true;
          setPendingConfirmation({
            ...needsConfirmation,
            precedingResults: allResults,
          });
          setWorking('Waiting for confirm');
          updateLastMessage({
            content: lastMsg.content || 'Confirm below to continue.',
            toolResults: allResults,
            isLoading: true,
            status: 'Waiting for confirm',
          });
          return;
        }

        try {
          await sendToolResultsToAI(allResults, lastMsg);
        } catch (followUpError: any) {
          if (isAbortError(followUpError)) {
            updateLastMessage({
              toolResults: allResults,
              content: lastMsg.content || 'Stopped.',
              isLoading: false,
              status: null,
            });
            return;
          }
          updateLastMessage({ toolResults: allResults });
          setMessages((prev) => [
            ...prev,
            {
              id: generateId(),
              role: 'assistant',
              content: summarizeCompletedTools(allResults),
              timestamp: new Date(),
            },
          ]);
        }
        return;
      }

      try {
        await sendToolResultsToAI(results, lastMsg);
      } catch (followUpError: any) {
        if (isAbortError(followUpError)) {
          updateLastMessage({
            toolResults: results,
            content: lastMsg.content || 'Stopped.',
            isLoading: false,
            status: null,
          });
          return;
        }
        updateLastMessage({ toolResults: results });
        setMessages((prev) => [
          ...prev,
          {
            id: generateId(),
            role: 'assistant',
            content: summarizeCompletedTools(results),
            timestamp: new Date(),
          },
        ]);
      }
    } catch (error: any) {
      if (isAbortError(error)) {
        updateLastMessage({
          content: 'Stopped.',
          isLoading: false,
          status: null,
        });
        return;
      }

      if (isNetworkFailure(error)) {
        setError(error.message);
        setFailedAction({ type: 'confirm', pending: confirmationSnapshot, reason: 'network' });
        updateLastMessage({
          content: 'Could not reach the service. Tap Retry when you are back online.',
          isLoading: false,
          status: null,
        });
      } else {
        // Non-network failure — recover via agent message, not Retry
        const lastMsg = messagesRef.current[messagesRef.current.length - 1];
        const failResult: ToolResultDisplay = {
          toolName: confirmationSnapshot.toolCall.function.name,
          toolCallId: confirmationSnapshot.toolCall.id,
          result: {
            success: false,
            data: null,
            error: error.message || 'The action failed.',
          },
        };
        const failResults = [...(precedingResults || []), failResult];
        if (lastMsg) {
          try {
            await sendToolResultsToAI(failResults, lastMsg);
          } catch {
            updateLastMessage({
              content: error.message || 'That did not work. Please try again or change the details.',
              toolResults: failResults,
              isLoading: false,
              status: null,
            });
          }
        }
      }
    } finally {
      setIsProcessing(false);
      if (!pausedForConfirm) {
        setWorking(null);
      }
    }
  }, [
    pendingConfirmation,
    executeTool,
    processToolCalls,
    sendToolResultsToAI,
    updateLastMessage,
    queryClient,
    setWorking,
  ]);

  /**
   * Cancel a pending action
   */
  const cancelAction = useCallback(() => {
    setPendingConfirmation(null);
    setFailedAction(null);
    setWorking(null);
    setIsProcessing(false);

    const cancelMsg: ChatMessageDisplay = {
      id: generateId(),
      role: 'assistant',
      content: 'Cancelled. What would you like to do instead?',
      timestamp: new Date(),
      isLoading: false,
      status: null,
    };
    updateLastMessage({ isLoading: false, status: null });
    setMessages((prev) => [...prev, cancelMsg]);
  }, [setWorking, updateLastMessage]);

  /**
   * Stop an in-progress AI run
   */
  const stopGeneration = useCallback(() => {
    cancelledRef.current = true;
    abortRef.current?.abort();
    setPendingConfirmation(null);
    setIsProcessing(false);
    setWorking(null);
    setAgentStatus(null);
    setMessages((prev) =>
      prev.map((m) =>
        m.isLoading
          ? {
              ...m,
              isLoading: false,
              status: null,
              content: m.content?.trim() ? m.content : 'Stopped.',
            }
          : m
      )
    );
  }, [setWorking]);

  /**
   * Retry the last failed send or confirm (network failures only)
   */
  const retryLastAction = useCallback(async () => {
    if (!failedAction || processingRef.current) return;
    if (failedAction.reason !== 'network') return;
    const action = failedAction;
    setFailedAction(null);
    setError(null);

    if (action.type === 'message') {
      await sendMessageRef.current?.(action.content);
      return;
    }

    setPendingConfirmation(action.pending);
    setWorking('Waiting for confirm');
  }, [failedAction, setWorking]);

  /**
   * Send a user message and get AI response
   */
  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim() || processingRef.current) return;

    beginRun();
    setPendingConfirmation(null);

    const userMsg: ChatMessageDisplay = {
      id: generateId(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
    };
    const loadingId = generateId();
    const loadingMsg: ChatMessageDisplay = {
      id: loadingId,
      role: 'assistant',
      content: '',
      isLoading: true,
      status: 'Thinking',
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMsg, loadingMsg]);
    setIsProcessing(true);
    setAgentStatus('Thinking');

    const finishAssistant = (msg: ChatMessageDisplay) => {
      setMessages((prev) => prev.map((m) => (m.id === loadingId ? { ...msg, id: loadingId } : m)));
    };

    let pausedForConfirm = false;

    try {
      const history = buildChatHistory();
      history.push({ role: 'user', content: content.trim() });

      const response = await aiProvider.chat({
        messages: history,
        tools: allTools,
      }, token || undefined, chatSignal());

      if (cancelledRef.current) {
        patchMessage(loadingId, {
          isLoading: false,
          status: null,
          content: 'Stopped.',
        });
        return;
      }

      const responseMsg = response.choices[0]?.message;
      const assistantMsg: ChatMessageDisplay = {
        id: loadingId,
        role: 'assistant',
        content: responseMsg.content || '',
        timestamp: new Date(),
        toolCalls: responseMsg.tool_calls,
        isLoading: !!(responseMsg.tool_calls && responseMsg.tool_calls.length),
        status: responseMsg.tool_calls?.length ? 'Working' : null,
      };
      finishAssistant(assistantMsg);

      if (responseMsg.tool_calls && responseMsg.tool_calls.length > 0) {
        try {
          pausedForConfirm = await handleToolCallsInMessage(assistantMsg);
        } catch (toolError: any) {
          if (isAbortError(toolError)) {
            patchMessage(loadingId, {
              isLoading: false,
              status: null,
              content: 'Stopped.',
            });
            return;
          }
          if (isNetworkFailure(toolError)) {
            patchMessage(loadingId, {
              isLoading: false,
              status: null,
              content: 'Could not reach the service. Tap Retry when you are back online.',
            });
            setFailedAction({ type: 'message', content: content.trim(), reason: 'network' });
          } else {
            patchMessage(loadingId, {
              isLoading: false,
              status: null,
              content: 'Something went wrong while finishing that. Please try again.',
            });
          }
        }
      }
    } catch (error: any) {
      if (isAbortError(error)) {
        patchMessage(loadingId, {
          isLoading: false,
          status: null,
          content: 'Stopped.',
        });
        return;
      }

      if (error.message && !error.message.includes('not configured') && !error.message.includes('Authentication')) {
        try {
          setAgentStatus('Trying again');
          patchMessage(loadingId, { isLoading: true, status: 'Trying again' });
          const recoveryHistory: ChatMessage[] = [];
          recoveryHistory.push({
            role: 'system',
            content: assembleSystemPrompt(promptContext()),
          });
          recoveryHistory.push({
            role: 'user',
            content: content.trim(),
          });

          const recoveryResponse = await aiProvider.chat({
            messages: recoveryHistory,
            tools: allTools,
          }, token || undefined, chatSignal());

          if (cancelledRef.current) {
            patchMessage(loadingId, {
              isLoading: false,
              status: null,
              content: 'Stopped.',
            });
            return;
          }

          const recoveryMsg = recoveryResponse.choices[0]?.message;
          const assistantMsg: ChatMessageDisplay = {
            id: loadingId,
            role: 'assistant',
            content: recoveryMsg.content || '',
            timestamp: new Date(),
            toolCalls: recoveryMsg.tool_calls,
            isLoading: !!(recoveryMsg.tool_calls && recoveryMsg.tool_calls.length),
            status: recoveryMsg.tool_calls?.length ? 'Working' : null,
          };
          finishAssistant(assistantMsg);

          if (recoveryMsg.tool_calls && recoveryMsg.tool_calls.length > 0) {
            pausedForConfirm = await handleToolCallsInMessage(assistantMsg);
          }
          return;
        } catch (recoveryError: any) {
          if (isAbortError(recoveryError)) {
            patchMessage(loadingId, {
              isLoading: false,
              status: null,
              content: 'Stopped.',
            });
            return;
          }
          // Fall through to error path
        }
      }

      const network = isNetworkFailure(error);
      patchMessage(loadingId, {
        isLoading: false,
        status: null,
        content: network
          ? 'Could not reach the service. Tap Retry when you are back online.'
          : `I could not complete that. ${error.message || 'Please try again.'}`,
      });
      setError(error.message);
      if (network) {
        setFailedAction({ type: 'message', content: content.trim(), reason: 'network' });
      }
    } finally {
      setIsProcessing(false);
      if (!pausedForConfirm) {
        setAgentStatus(null);
      }
    }
  }, [buildChatHistory, handleToolCallsInMessage, token, promptContext, patchMessage]);

  sendMessageRef.current = sendMessage;
  confirmActionRef.current = confirmAction;

  /**
   * Clear the chat history (and remove persisted messages)
   */
  const clearChat = useCallback(() => {
    setMessages([]);
    setPendingConfirmation(null);
    setError(null);
    setFailedAction(null);
    setEditingMessageId(null);
    try {
      localStorage.removeItem(STORAGE_MESSAGES_KEY);
    } catch {}
  }, []);

  /**
   * Start editing a user message.
   * When a user message is edited, ALL messages after it are removed,
   * and the user can send a new message to continue the conversation from that point.
   */
  const startEditingMessage = useCallback((messageId: string) => {
    setEditingMessageId(messageId);
  }, []);

  /**
   * Cancel editing and restore the original messages
   */
  const cancelEditing = useCallback(() => {
    setEditingMessageId(null);
  }, []);

  /**
   * Replace all messages from a given point onwards.
   * Updates the user's message with new content and removes all subsequent messages.
   * Then automatically sends the edited message.
   */
  const editMessageAndContinue = useCallback(async (messageId: string, newContent: string) => {
    if (!newContent.trim() || isProcessing) return;

    setEditingMessageId(null);

    // Find the message index
    const currentMessages = messagesRef.current;
    const msgIndex = currentMessages.findIndex(m => m.id === messageId);
    if (msgIndex < 0) return;

    // Keep messages up to (and including) the edited message
    const keptMessages = currentMessages.slice(0, msgIndex + 1);
    
    // Update the user's message content
    keptMessages[msgIndex] = {
      ...keptMessages[msgIndex],
      content: newContent.trim(),
    };

    // Set messages to truncated version + new user message
    // We'll append a new user message with the edited content
    // so the AI can re-process it fresh
    setMessages(keptMessages);

    setIsProcessing(true);
    setError(null);

    try {
      // Build fresh history from the truncated messages
      // but replace the edited message content
      const history: ChatMessage[] = [];

      history.push({
        role: 'system',
        content: assembleSystemPrompt(promptContext()),
      });

      // Add conversation history up to and including the edited message
      for (const msg of keptMessages) {
        if (msg.role === 'user') {
          history.push({ role: 'user', content: msg.content });
        } else if (msg.role === 'assistant') {
          const pairs =
            msg.toolCalls?.length && msg.toolResults?.length
              ? pairToolResults(msg.toolCalls, msg.toolResults)
              : [];
          if (pairs.length > 0) {
            history.push({
              role: 'assistant',
              content: msg.content || '',
              tool_calls: pairs.map((p) => p.toolCall),
            });
            for (const pair of pairs) {
              history.push({
                role: 'tool',
                content: JSON.stringify(pair.result),
                tool_call_id: pair.toolCall.id,
              });
            }
          } else if ((msg.content || '').trim()) {
            history.push({ role: 'assistant', content: msg.content });
          }
        }
      }

      // Call AI with the edited history
      const response = await aiProvider.chat({
        messages: sanitizeHistoryForTools(history),
        tools: allTools,
      }, token || undefined, chatSignal());

      const responseMsg = response.choices[0]?.message;

      const assistantMsg: ChatMessageDisplay = {
        id: generateId(),
        role: 'assistant',
        content: responseMsg.content || '',
        timestamp: new Date(),
        toolCalls: responseMsg.tool_calls,
      };

      setMessages(prev => [...prev, assistantMsg]);

      // If the AI wants to call tools, handle them
      if (responseMsg.tool_calls && responseMsg.tool_calls.length > 0) {
        try {
          await handleToolCallsInMessage(assistantMsg);
        } catch (toolError: any) {
          const recoveryMsg: ChatMessageDisplay = {
            id: generateId(),
            role: 'assistant',
            content: 'Something went wrong while finishing that. Please try again.',
            timestamp: new Date(),
          };
          setMessages(prev => [...prev, recoveryMsg]);
        }
      }
    } catch (error: any) {
      const errorMsg: ChatMessageDisplay = {
        id: generateId(),
        role: 'assistant',
        content: 'Sorry — something went wrong. Please try again.',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMsg]);
      setError(error.message);
    } finally {
      setIsProcessing(false);
    }
  }, [isProcessing, user, getLessonsContextString, handleToolCallsInMessage, token]);

  const consumeComposerDraft = useCallback(() => {
    setComposerDraft('');
  }, []);

  const toggleOpen = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const openChat = useCallback((prefill?: string | unknown) => {
    if (typeof prefill === 'string' && prefill.trim()) {
      setComposerDraft(prefill.trim());
    }
    setIsOpen(true);
  }, []);

  const closeChat = useCallback(() => {
    setIsOpen(false);
    setPendingConfirmation(null);
    setEditingMessageId(null);
  }, []);

  return {
    messages,
    isProcessing,
    agentStatus,
    pendingConfirmation,
    isOpen,
    error,
    failedAction,
    editingMessageId,
    composerDraft,
    consumeComposerDraft,
    sendMessage,
    clearChat,
    toggleOpen,
    openChat,
    closeChat,
    confirmAction,
    cancelAction,
    stopGeneration,
    retryLastAction,
    startEditingMessage,
    cancelEditing,
    editMessageAndContinue,
  };
}
